import { Card } from '@material-ui/core'
import clsx from 'clsx'
import { TrackChangeStatus } from 'models/changes'
import React, { forwardRef, useRef } from 'react'
import { useScrollStoreShallowSelector } from '../../../services/ScrollToTreeNode/store/scrollStore'
import { TrackChangesCardProps } from '../../../types/types'
import { useCardType } from './hooks/useCardType'
import { useCombinedRefs } from './hooks/useCombinedRefs'
import { useTrackChangeCardClickHandler } from './hooks/useTrackChangeCardClickHandler'
import { TrackChangeCardBody } from './TrackChangeCardBody'
import { TrackChangeCardHeader } from './TrackChangeCardHeader'

/**
 * TrackChangeCard component renders a card for track changes with header and body.
 * @param {TrackChangesCardProps} props - The properties for the TrackChangeCard component.
 * @returns {JSX.Element | null} The rendered track change card or null if not applicable.
 */
export const TrackChangeCard = React.memo(
    forwardRef<HTMLDivElement, TrackChangesCardProps>(
        (
            {
                cardSuggestion,
                trackChanges,
                isNotDefaultSuggestionCardFilter = false,
            },
            ref
        ) => {
            const { cardType, classes } = useCardType(
                cardSuggestion,
                isNotDefaultSuggestionCardFilter,
                trackChanges
            )

            const shouldRender =
                isNotDefaultSuggestionCardFilter ||
                trackChanges?.status === TrackChangeStatus.PENDING
            const {
                setScrollToContent,
                setIsHandlingChangeTab,
            } = useScrollStoreShallowSelector((state) => ({
                setScrollToContent: state.setScrollToContent,
                setIsHandlingChangeTab: state.setIsHandlingChangeTab,
            }))

            // Use a ref to track the card element
            const cardRef = useRef<HTMLDivElement>(null)
            const combinedRef = useCombinedRefs(cardRef, ref)
            const onCardClick = useTrackChangeCardClickHandler(
                cardRef,
                cardSuggestion?.id,
                isNotDefaultSuggestionCardFilter,
                trackChanges,
                setScrollToContent,
                setIsHandlingChangeTab
            )

            if (!shouldRender) return null

            return (
                <Card
                    ref={combinedRef}
                    className={clsx(classes.card, 'trackChangesCard', {
                        visible: isNotDefaultSuggestionCardFilter,
                    })}
                    id={cardSuggestion?.id}
                    data-suggestion-id={cardSuggestion?.id}
                    data-testid={cardSuggestion?.id}
                    aria-label="track-change-card"
                    onClick={onCardClick}
                >
                    <TrackChangeCardHeader
                        trackChanges={trackChanges}
                        cardSuggestion={cardSuggestion}
                        classes={classes}
                        cardType={cardType}
                    />
                    <TrackChangeCardBody
                        cardType={cardType}
                        cardSuggestion={cardSuggestion}
                        classes={classes}
                    />
                </Card>
            )
        }
    )
)
